(() => {
  'use strict';
  if (window.ObjectivIngredientExplorer) { window.ObjectivIngredientExplorer.mount(document); return; }

  class IngredientExplorer {
    constructor(element) {
      this.element = element;
      this.points = [...element.querySelectorAll('[data-ingredient-point]')];
      this.panels = [...element.querySelectorAll('[data-ingredient-panel]')];
      if (this.points.length !== 8 || this.panels.length !== 8) return;
      this.interval = Number(element.dataset.ingredientInterval) || 3000;
      this.index = 0;
      this.remaining = this.interval;
      this.visible = false;
      this.reading = false;
      this.keyboardFocused = false;
      this.abort = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.fullList = element.querySelector('[data-ingredient-full-list]');
      this.status = element.querySelector('[data-ingredient-status]');
      const options = {signal: this.abort.signal};
      element.dataset.ingredientEnhanced = '';
      const tabs = element.querySelector('.obj-ingredients__points');
      tabs.setAttribute('role', 'tablist');
      this.points.forEach((point, index) => {
        point.disabled = false;
        point.setAttribute('role', 'tab');
        point.addEventListener('click', () => this.select(index, true), options);
        point.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % 8;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + 7) % 8;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = 7;
          if (next === undefined) return;
          event.preventDefault();
          this.select(next, true);
          this.points[next].focus({preventScroll: true});
        }, options);
      });
      this.panels.forEach((panel, index) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.points[index].id);
      });
      const copy = element.querySelector('.obj-ingredients__copy');
      copy.tabIndex = 0;
      copy.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { this.reading = true; this.sync(); } }, options);
      copy.addEventListener('pointerleave', () => { this.reading = false; this.sync(); }, options);
      element.addEventListener('focusin', event => { this.keyboardFocused = event.target.matches(':focus-visible'); this.sync(); }, options);
      element.addEventListener('focusout', event => { if (!element.contains(event.relatedTarget)) { this.keyboardFocused = false; this.sync(); } }, options);
      this.fullList.addEventListener('toggle', () => this.sync(), options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.sync(), options);
      const orbit = element.querySelector('[data-ingredient-orbit]');
      this.orbit = orbit.animate?.([{transform: 'rotate(0deg)'}, {transform: 'rotate(360deg)'}], {duration: this.interval * 8, iterations: Infinity});
      this.orbit?.pause();
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .15;
        this.sync();
      }, {threshold: [0, .15]});
      this.observer.observe(element);
      element.objectivIngredientExplorer = this;
      this.select(0);
    }

    select(index, manual = false) {
      if (this.destroyed) return;
      this.stop();
      this.index = index;
      this.remaining = this.interval;
      this.points.forEach((point, number) => {
        point.setAttribute('aria-selected', String(number === index));
        point.tabIndex = number === index ? 0 : -1;
        const panel = this.panels[number];
        panel.setAttribute('aria-hidden', String(number !== index));
        panel.inert = number !== index;
      });
      this.element.dataset.ingredientActive = String(index);
      if (manual) this.status.textContent = this.panels[index].querySelector('h3').textContent;
      if (this.orbit) this.orbit.currentTime = index * this.interval;
      this.sync();
    }

    stop() {
      if (this.timer !== undefined) {
        clearTimeout(this.timer);
        this.remaining = Math.max(0, this.deadline - performance.now());
        this.timer = undefined;
      }
      this.orbit?.pause();
    }

    sync() {
      if (this.destroyed) return;
      const running = this.visible && !document.hidden && !this.reading && !this.keyboardFocused && !this.motion.matches && !this.fullList.open;
      this.element.toggleAttribute('data-ingredient-running', running);
      if (!running) { this.stop(); return; }
      if (this.timer !== undefined) return;
      this.orbit?.play();
      this.deadline = performance.now() + this.remaining;
      this.timer = setTimeout(() => {
        this.timer = undefined;
        this.select((this.index + 1) % 8);
      }, this.remaining);
    }

    destroy() {
      this.destroyed = true;
      this.stop();
      this.orbit?.cancel();
      this.abort.abort();
      this.observer.disconnect();
      delete this.element.objectivIngredientExplorer;
    }

    static mount(root) {
      const elements = [...root.querySelectorAll('[data-ingredient-explorer]')];
      if (root.matches?.('[data-ingredient-explorer]')) elements.unshift(root);
      elements.forEach(element => { if (!element.objectivIngredientExplorer) new IngredientExplorer(element); });
    }
  }
  window.ObjectivIngredientExplorer = IngredientExplorer;
  IngredientExplorer.mount(document);
  document.addEventListener('shopify:section:load', event => IngredientExplorer.mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-ingredient-explorer]').forEach(element => element.objectivIngredientExplorer?.destroy());
  });
})();
